import React from "react";
import { connect } from "react-redux";
import {
  IAppState,
  ErrorDisplay,
  LoadingDisplay,
  LoadingState,
  ResponseError,
  ApiRequest
} from "../../../../Common";
import { Application, ApplicationQuickMetrics, endpoints } from "../../..";
import AppSummaryGrid from "./AppSummaryGrid";
import ResponseHandler from "../../../ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";

type Props = {};

type StateProps = {
  apps: Application[];
  appsLoadingState: LoadingState;
  appsLoadingError: ResponseError;
};

type State = {
  appMetricsLoadingState: LoadingState;
  appMetricsLoadingError: ResponseError;
  loadedMetrics: Record<string, ApplicationQuickMetrics>;
};

function mapStateToProps(state: IAppState): StateProps {
  return {
    apps: state.app.applications,
    appsLoadingState: state.app.appsLoadingState,
    appsLoadingError: state.app.appsLoadingError
  };
}

class AppSummaryGridContainer extends React.Component<Props & StateProps, State> {
  constructor(props: Props & StateProps) {
    super(props);

    this.state = {
      appMetricsLoadingState: {
        isLoaded: false,
        isLoading: false,
        errored: false
      },
      appMetricsLoadingError: {
        errorDescription: "",
        errorResult: ""
      },
      loadedMetrics: {}
    };
  }

  componentDidMount() {
    this.loadIfNeeded();
  }

  componentDidUpdate() {
    this.loadIfNeeded();
  }

  setLoaded(loadedMetrics: Record<string, ApplicationQuickMetrics>) {
    this.setState((prevState: State) => ({
      appMetricsLoadingState: {
        errored: false,
        isLoading: false,
        isLoaded: true
      },
      loadedMetrics
    }));
  }

  setErrored(statusCode: number, errorDesc: string) {
    this.setState((prevState: State) => ({
      appMetricsLoadingState: {
        errored: true,
        isLoading: false,
        isLoaded: false
      },
      appMetricsLoadingError: {
        errorCode: statusCode,
        errorDescription: errorDesc,
        errorResult: "Could not display the app metrics"
      }
    }));
  }

  loadIfNeeded() {
    const { isLoaded, isLoading, errored } = this.state.appMetricsLoadingState;
    if (this.props.appsLoadingState.isLoaded && !isLoaded && !isLoading && !errored) {
      this.load();
    }
  }

  load() {
    // set state to loading
    this.setState(
      (prevState: State) => ({
        appMetricsLoadingState: {
          isLoaded: false,
          isLoading: true,
          errored: false
        },
        loadedMetrics: {}
      }),
      () => {
        // make the API request to get the metrics for this app
        ApiRequest.fromPath(endpoints.applications.allMetrics())
          .withAuthHeader()
          .withJson({
            RolePrefixes: this.props.apps.map(a => a.rolePrefix)
          })
          .post()
          .then((response: Response) => {
            ResponseHandler.handle(
              response,
              (result: ResponseResult) => {
                this.setLoaded(result.payload);
              },
              (result: ResponseResult) => {
                this.setErrored(response.status, result.error.message);
              },
              () => {
                this.setErrored(response.status, "An unknown error has occured");
              }
            );
          });
      }
    );
  }

  render() {
    if (this.props.appsLoadingState.errored) {
      return (
        <ErrorDisplay
          errorCode={this.props.appsLoadingError.errorCode}
          errorDescription={this.props.appsLoadingError.errorDescription}
          errorResult="Could not display the app summary grid- could not load apps"
        />
      );
    }
    if (this.state.appMetricsLoadingState.errored) {
      return (
        <ErrorDisplay
          errorCode={this.state.appMetricsLoadingError.errorCode}
          errorDescription={this.state.appMetricsLoadingError.errorDescription}
          errorResult="Could not display the app summary grid- could not load metrics"
        />
      );
    }
    if (this.props.appsLoadingState.isLoading) {
      return <LoadingDisplay />;
    }
    if (this.props.appsLoadingState.isLoaded) {
      return (
        <AppSummaryGrid
          apps={this.props.apps}
          appMetrics={this.state.loadedMetrics}
          appMetricsLoadingState={this.state.appMetricsLoadingState}
        />
      );
    }
    return <></>;
  }
}

export default connect(mapStateToProps)(AppSummaryGridContainer);
